import type { Target } from '../../api/communication';

/**
 * LA traduction, en un seul endroit, des cibles `{kind, id}` de l'API de
 * communication en routes du front (D67).
 *
 * L'API ne parle jamais de routes : elle sert aussi l'application mobile, qui
 * aura les siennes. Un genre inconnu mène à la liste des notifications plutôt
 * que nulle part.
 */
export function targetRoute(target: Target | null | undefined, permissions: readonly string[] = []): string {
    if (target === null || target === undefined) return '/notifications';

    const id = target.id === null || target.id === undefined || target.id === '' ? null : encodeURIComponent(target.id);

    switch (target.kind) {
        case 'notification':
            return id === null ? '/notifications' : `/notifications/${id}`;
        case 'conversation':
            return id === null ? '/messages' : `/messages/${id}`;
        case 'payroll_month':
            return id === null ? '/payroll' : `/payroll/${id}`;
        case 'teacher':
            return id === null ? '/faculty/teachers' : `/faculty/teachers/${id}`;
        case 'attendance_dashboard':
            return '/attendance/dashboard';
        case 'roll_calls':
        case 'attendance_rolls':
            return id === null ? '/attendance/roll-call' : `/attendance/roll-call/${id}`;
        case 'my_assessments':
        case 'assessment':
            // Un enseignant passe par « Mes évaluations » ; le reste du personnel par la liste.
            if (id === null) return permissions.includes('assessment.read') ? '/assessment/evaluations' : '/assessment/mine';

            return permissions.includes('assessment.read') ? `/assessment/evaluations/${id}` : `/assessment/mine/${id}`;
        case 'faculty_contracts':
        case 'contract':
            return '/faculty/contracts';
        // D68 : « convention à échéance » et « piste sans échange ».
        case 'agreement':
            return id === null ? '/relations/agreements?filter=ending' : `/relations/agreements/${id}`;
        case 'prospect':
            return '/relations/prospects';
        // D69 : les étapes d'un stage (étudiant), l'encadrement, les stages proposés, les offres.
        case 'my_internship':
            return id === null || !permissions.includes('stages.read') ? '/stages/mine' : `/stages/internships/${id}`;
        case 'supervised_internship':
            return id === null ? '/stages/supervised' : `/stages/internships/${id}`;
        case 'supervised_internships':
            return '/stages/supervised';
        case 'stage_internships':
            return '/stages/internships?filter=to_validate';
        case 'stage_offer':
            return permissions.includes('stages.read') && id !== null ? `/stages/offers/${id}` : '/stages/mine/offers';
        // D71 : une demande (le demandeur, l'équipe de l'étape), les demandes en retard.
        case 'my_request':
        case 'request':
            return id === null ? (permissions.includes('requests.handle') ? '/requests/list' : '/requests/mine') : `/requests/${id}`;
        case 'requests_late':
            return '/requests/list?view=late';
        // D72 : l'internat (le portail de l'étudiant, les demandes, les reçus, les réparations urgentes).
        case 'my_housing':
            return '/housing/mine';
        // D82 : la bibliothèque (le lecteur : prêt, relance, réservation disponible).
        case 'my_library':
            return '/library/mine';
        case 'housing_applications':
            return '/housing/campaigns';
        case 'housing_payments':
            return '/housing/payments';
        case 'housing_repairs':
            return '/housing/upkeep?tab=repairs';
        // D73 : le portail enseignant (RIB vérifié ou refusé, écart d'heures
        // traité) et, pour la planification, les écarts signalés.
        case 'teacher_profile':
            return '/teach/profile';
        case 'teacher_hours':
            return '/teach/hours';
        case 'hours_reports':
            return '/planning/hours';
        // D74 : les compétences (dépôts reçus par l'enseignant ; demande de
        // dépôt et niveau final publié, pour l'étudiant).
        case 'skills_activity':
            return id === null ? '/teach/skills' : `/skills/activities/${id}`;
        case 'my_skills':
            return id === null ? '/me/skills' : `/me/skills/${id}`;
        // D76 : l'E-Learning (le cours de l'étudiant, le cours de l'enseignant,
        // les sujets à valider, les lots de notes).
        case 'my_course':
            return id === null ? '/me/courses' : `/me/courses/${id}`;
        case 'teacher_course':
            return id === null ? '/teach/courses' : `/teach/courses/${id}?tab=grades`;
        case 'learning_subjects':
            return id === null ? '/learning/subjects' : `/teach/quizzes/${id}`;
        case 'learning_transfers':
            return id === null ? '/learning/transfers' : `/learning/transfers?quiz=${id}`;
        // D80 : la fin d'un lot de production, les lots à signer.
        case 'document_job':
            return id === null ? '/documents/jobs' : `/documents/jobs/${id}`;
        case 'documents_to_sign':
            return '/documents/sign';
        case 'shared_document':
            return permissions.includes('portal.documents') ? '/me/documents' : '/me/shared';
        default:
            return '/notifications';
    }
}
